/**
 * <CountryPicker value onChange /> — combobox that replaces the native country
 * <select>. Shows the selected country's SVG flag + name in a button, opens a
 * searchable list of all 250 ISO entries (+ Kurdistan), supports alias search
 * (USA, UK, Holland, Burma, etc.) via resolveCountry / COUNTRY_ALIASES, keyboard
 * navigation, and click-outside to close.
 *
 * Stored value is the ISO alpha-2 code (string). `onChange(code)` fires with
 * the new code. Set `required` to surface the same UX cue the native control
 * had, and pass `data-testid` straight through.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X, Check } from "lucide-react";
import { COUNTRY_LIST, countryName, flagFromCode, COUNTRY_ALIASES } from "../lib/countries";

// Build reverse alias index: code → list of alternate names users might type.
const ALIASES_BY_CODE = (() => {
  const map = {};
  for (const [alias, code] of Object.entries(COUNTRY_ALIASES || {})) {
    (map[code] = map[code] || []).push(alias);
  }
  return map;
})();

function entryMatches(entry, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  if (entry.code.toLowerCase().startsWith(needle)) return true;
  if (entry.name.toLowerCase().includes(needle)) return true;
  const aliases = ALIASES_BY_CODE[entry.code] || [];
  return aliases.some((a) => a.includes(needle));
}

export default function CountryPicker({
  value,
  onChange,
  placeholder = "Pick a country",
  disabled = false,
  required = false,
  testId = "country-picker",
  invalid = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [focusIdx, setFocusIdx] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(() => {
    if (!value) return null;
    return COUNTRY_LIST.find((c) => c.code === String(value).toUpperCase()) || null;
  }, [value]);

  const filtered = useMemo(
    () => COUNTRY_LIST.filter((c) => entryMatches(c, q)),
    [q]
  );

  // Reset highlight when filter changes; scroll active item into view.
  useEffect(() => { setFocusIdx(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-idx="${focusIdx}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [focusIdx, open]);

  // Auto-focus search field when opened.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);

  // Click outside to close.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const pick = (code) => {
    onChange(code);
    setOpen(false);
    setQ("");
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = filtered[focusIdx];
      if (c) pick(c.code);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const triggerCls =
    `mt-1 w-full px-3 py-3 rounded-lg bg-zinc-900 border outline-none transition flex items-center gap-2.5 text-left
     ${invalid ? "border-red-500/60 focus:border-red-400" : "border-white/10 focus:border-[var(--accent)]"}
     ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-zinc-900/70"}`;

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required || undefined}
        onClick={() => !disabled && setOpen((v) => !v)}
        data-testid={`${testId}-trigger`}
        className={triggerCls}
      >
        {selected ? (
          <>
            <span data-testid={`${testId}-selected-flag`} className="shrink-0">
              {flagFromCode(selected.code, { size: "1.5em" })}
            </span>
            <span className="flex-1 truncate text-sm" data-testid={`${testId}-selected-name`}>
              {selected.name}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">{selected.code}</span>
          </>
        ) : (
          <span className="flex-1 truncate text-sm text-zinc-500">{placeholder}</span>
        )}
        <ChevronDown size={16} className={`shrink-0 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid={`${testId}-panel`}
          className="absolute z-30 mt-1.5 left-0 right-0 rounded-xl border border-white/10 bg-[var(--surface)] shadow-2xl overflow-hidden"
        >
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/5">
            <Search size={14} className="text-zinc-500 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKey}
              placeholder="Search country or type USA, UK, Holland…"
              data-testid={`${testId}-search`}
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-zinc-600"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Clear search"
                className="text-zinc-500 hover:text-zinc-200"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div
            ref={listRef}
            data-testid={`${testId}-options`}
            className="max-h-72 overflow-y-auto py-1"
          >
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-zinc-500" data-testid={`${testId}-empty`}>
                No countries match “{q}”.
              </div>
            )}
            {filtered.map((c, idx) => {
              const isSel = selected?.code === c.code;
              const isFocus = idx === focusIdx;
              return (
                <button
                  type="button"
                  key={c.code}
                  data-idx={idx}
                  data-testid={`${testId}-option-${c.code}`}
                  role="option"
                  aria-selected={isSel}
                  onMouseEnter={() => setFocusIdx(idx)}
                  onClick={() => pick(c.code)}
                  className={`w-full px-3 py-2 flex items-center gap-2.5 text-left text-sm transition
                    ${isFocus ? "bg-[var(--accent)]/12" : ""}
                    ${isSel ? "text-[var(--accent)]" : "text-zinc-200"}`}
                >
                  <span className="shrink-0">{flagFromCode(c.code, { size: "1.4em" })}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">{c.code}</span>
                  {isSel && <Check size={14} className="text-[var(--accent)]" />}
                </button>
              );
            })}
          </div>
          <div className="px-3 py-2 border-t border-white/5 text-[10px] uppercase tracking-widest text-zinc-600 flex justify-between">
            <span>{filtered.length} of {COUNTRY_LIST.length}</span>
            <span>↑ ↓ to navigate · enter to select</span>
          </div>
        </div>
      )}

      {/* Used by form-validation libraries / native required: a hidden input
       * mirrors the selected code so the surrounding <form> still validates. */}
      {required && (
        <input
          tabIndex={-1}
          aria-hidden
          value={value || ""}
          onChange={() => {}}
          required
          className="sr-only"
          data-testid={`${testId}-mirror`}
        />
      )}
    </div>
  );
}

export { CountryPicker };
